import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import AnimationRevealPage from "../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import Header from "../Components/headers/PageHeader";

// Styled Components
const Container = styled.div`
  ${tw`mx-auto px-4 md:px-6 xl:mx-20 py-12`}
`;
const Meta = styled.div`
  ${tw`flex justify-between items-center gap-4 text-sm text-gray-500 mt-4`}
`;
const Author = styled.span`
  ${tw`text-gray-700`}
`;
const DateText = styled.span`
  ${tw`text-gray-500 text-xs`}
`;
const Title = styled.h1`
  ${tw`text-3xl xl:text-5xl text-left font-bold text-gray-900 leading-tight mt-2 mb-6`}
`;
const Content = styled.div`
  ${tw`max-w-5xl text-gray-700 justify-center mx-auto text-left leading-relaxed`}
`;
const MetaDescription = styled.p`
  ${tw`text-lg text-gray-600 mt-2 max-w-2xl text-right`}
`;
const Blockquote = styled.blockquote`
  ${tw`border-l-4 border-indigo-600 pl-4 italic text-gray-600 my-6`}
`;
const CTASection = styled.div`
  ${tw`text-center bg-indigo-600 text-white p-8 rounded-2xl shadow-md my-12`}
`;
const StyledButton = styled.a`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: #ffffff;
  color: #4338ca;
  border-radius: 0.5rem;
  font-weight: 600;
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: #f3f4f6;
  }
`;
const StyledCTAHeading = styled.h2`
  ${tw`text-2xl font-semibold mb-4`}
`;
const StyledImage = styled.img`
  ${tw`w-full h-full mt-6 md:mt-0 object-contain`}
`;
const StyledHR = styled.hr`
  ${tw`my-8 border-gray-300`}
`;
const StyledParagraph = styled.p`
  ${tw`mb-4 leading-relaxed`}
`;
const StyledLoadingContainer = styled.div`
  ${tw`flex justify-center items-center py-20`}
  min-height: 100vh;
`;
const StyledLoader = styled.div`
  border: 4px solid #e5e7eb; /* gray-200 */
  border-top: 4px solid #ff5464; /* indigo-700 */
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
const StyledError = styled.p`
  ${tw`text-center py-20 text-red-600`}
`;
const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;
const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
const RightColumn = styled.div`
  text-align: right;
`;

// Styled Components for Lists
const StyledList = styled.ul`
  ${tw`pl-6 mb-6 list-disc`}
`;
const StyledOrderedList = styled.ol`
  ${tw`pl-6 mb-6 list-decimal`}
`;

// Styled Components for Headings
const StyledH1 = styled.h1`
  ${tw`font-bold text-gray-900 mt-8 mb-4 text-3xl md:text-4xl`}
`;
const StyledH2 = styled.h2`
  ${tw`font-bold text-gray-900 mt-8 mb-4 text-2xl md:text-3xl`}
`;
const StyledH3 = styled.h3`
  ${tw`font-bold text-gray-900 mt-6 mb-3 text-xl md:text-2xl`}
`;
const StyledH4 = styled.h4`
  ${tw`font-bold text-gray-900 mt-6 mb-3 text-lg md:text-xl`}
`;
const StyledH5 = styled.h5`
  ${tw`font-bold text-gray-900 mt-4 mb-2 text-base md:text-lg`}
`;
const StyledH6 = styled.h6`
  ${tw`font-bold text-gray-900 mt-4 mb-2 text-sm md:text-base`}
`;

// Styled Components for FAQ Accordion
const Accordion = styled.div`
  ${tw`border border-gray-200 rounded-lg mb-4 overflow-hidden`}
`;
const AccordionHeader = styled.button`
  ${tw`w-full text-left px-4 py-3 bg-gray-100 hover:bg-gray-200 transition-colors flex justify-between items-center focus:outline-none`}
  font-weight: 600;
  color: #1f2937;
`;
const AccordionContent = styled.div`
  ${tw`px-4 py-3 bg-white`}
  ${({ isOpen }) => (isOpen ? tw`block` : tw`hidden`)}
`;

const SingleBlogPost = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();
  const [openAccordions, setOpenAccordions] = useState({});

  const websiteDomain = "milaajbrandset.com";
  const API_URL = "https://cms.milaaj.com";
  const singlePostUrl = `${API_URL}/api/blog-posts/by-slug/${encodeURIComponent(
    websiteDomain
  )}/${slug}`;

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(singlePostUrl, {
          params: { depth: 2, draft: false },
          headers: { "Content-Type": "application/json" },
        });

        if (!response.data) throw new Error("No blog post data returned");
        setPost(response.data);
      } catch (err) {
        console.error("Error fetching blog:", err.message);
        setError(err.response?.status === 404 ? "not-found" : "load-error");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, singlePostUrl]);

  const toggleAccordion = (index) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderRichText = (richText) => {
    if (!richText?.root?.children?.length) return <StyledParagraph>No content</StyledParagraph>;

    let faqMode = false;
    let faqItems = [];
    let faqIndex = null;
    // let faqHeading = null;

    const headingTags = {
      h1: StyledH1,
      h2: StyledH2,
      h3: StyledH3,
      h4: StyledH4,
      h5: StyledH5,
      h6: StyledH6,
    };

    const renderNode = (node, index) => {
      // Check if the node is a heading with "FAQ" (case-insensitive)
      const isFAQHeading = node.type === "heading" && 
        node.children?.some(child => child.text.toLowerCase().includes("faq"));

      if (isFAQHeading) {
        faqMode = true;
        faqItems = [];
        faqIndex = index;
        // faqHeading = node.children?.map((child) => child.text).join("");
        // Render the FAQ heading
        const Tag = headingTags[node.tag] || StyledH2;
        return (
          <Tag key={index}>
            {node.children?.map((child, i) => (
              <span
                key={i}
                css={
                  child.format === 1
                    ? tw`font-bold`
                    : child.format === 2
                    ? tw`italic`
                    : tw`font-normal`
                }
              >
                {child.text}
              </span>
            ))}
          </Tag>
        );
      }

      if (faqMode) {
        if (node.type === "heading") {
          // End FAQ mode if another heading is encountered
          faqMode = false;
          const faqContent = (
            <div key={faqIndex}>
              {faqItems.map((item, i) => (
                <Accordion key={i}>
                  <AccordionHeader onClick={() => toggleAccordion(i)}>
                    <span>{item.question}</span>
                    <span>{openAccordions[i] ? "−" : "+"}</span>
                  </AccordionHeader>
                  <AccordionContent isOpen={openAccordions[i]}>
                    <StyledParagraph>{item.answer}</StyledParagraph>
                  </AccordionContent>
                </Accordion>
              ))}
            </div>
          );
          faqItems = [];
          faqIndex = null;
          // faqHeading = null;
          // Render the new heading
          const Tag = headingTags[node.tag] || StyledH2;
          return [
            faqContent,
            <Tag key={index}>
              {node.children?.map((child, i) => (
                <span
                  key={i}
                  css={
                    child.format === 1
                      ? tw`font-bold`
                      : child.format === 2
                      ? tw`italic`
                      : tw`font-normal`
                  }
                >
                  {child.text}
                </span>
              ))}
            </Tag>,
          ];
        }

        if (node.type === "paragraph") {
          // Handle FAQ paragraph with question and answer separated by linebreak
          let question = "";
          let answer = "";
          let isQuestion = true;

          node.children.forEach((child) => {
            if (child.type === "linebreak") {
              isQuestion = false;
            } else if (child.type === "text") {
              if (isQuestion) {
                question += child.text;
              } else {
                answer += child.text;
              }
            }
          });

          // Fallback: If no linebreak, treat first text node as question, rest as answer
          if (question === "" && answer === "" && node.children.length) {
            const firstText = node.children.find((child) => child.type === "text");
            if (firstText) {
              question = firstText.text;
              answer = node.children
                .slice(node.children.indexOf(firstText) + 1)
                .filter((child) => child.type === "text")
                .map((child) => child.text)
                .join("");
            }
          }

          if (question.trim() && answer.trim()) {
            faqItems.push({ question: question.trim(), answer: answer.trim() });
          }
          return null;
        }
        return null;
      }

      // Non-FAQ content rendering
      switch (node.type) {
        case "heading":
          const Tag = headingTags[node.tag] || StyledH2;
          return (
            <Tag key={index}>
              {node.children?.map((child, i) => (
                <span
                  key={i}
                  css={
                    child.format === 1
                      ? tw`font-bold`
                      : child.format === 2
                      ? tw`italic`
                      : tw`font-normal`
                  }
                >
                  {child.text}
                </span>
              ))}
            </Tag>
          );
        case "paragraph":
          return (
            <StyledParagraph key={index}>
              {node.children.map((child, i) => {
                if (child.type === "text") {
                  return (
                    <span
                      key={i}
                      css={
                        child.format === 1
                          ? tw`font-bold`
                          : child.format === 2
                          ? tw`italic`
                          : tw`font-normal`
                      }
                    >
                      {child.text}
                    </span>
                  );
                }
                return null;
              })}
            </StyledParagraph>
          );
        case "list":
          const ListTag = node.listType === "bullet" ? StyledList : StyledOrderedList;
          return (
            <ListTag key={index}>
              {node.children?.map((li, i) => (
                <li key={i}>
                  {li.children?.map((c, j) => (
                    <span
                      key={j}
                      css={
                        c.format === 1
                          ? tw`font-bold`
                          : c.format === 2
                          ? tw`italic`
                          : tw`font-normal`
                      }
                    >
                      {c.text}
                    </span>
                  ))}
                </li>
              ))}
            </ListTag>
          );
        case "quote":
          return <Blockquote key={index}>{node.children?.map((c) => c.text).join("")}</Blockquote>;
        case "block":
          if (node.fields?.blockType === "callToAction") {
            return (
              <CTASection key={index}>
                <StyledCTAHeading>{node.fields.text}</StyledCTAHeading>
                <StyledButton
                  href={node.fields?.link || "#"}
                  target={node.fields?.openInNewTab ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                >
                  Learn More
                </StyledButton>
              </CTASection>
            );
          }
          return null;
        case "horizontalrule":
          return <StyledHR key={index} />;
        default:
          return null;
      }
    };

    // Render all nodes and handle any remaining FAQ content
    const renderedNodes = richText.root.children.map((node, i) => renderNode(node, i));
    if (faqMode && faqItems.length) {
      renderedNodes.push(
        <div key={faqIndex}>
          {faqItems.map((item, i) => (
            <Accordion key={i}>
              <AccordionHeader onClick={() => toggleAccordion(i)}>
                <span>{item.question}</span>
                <span>{openAccordions[i] ? "−" : "+"}</span>
              </AccordionHeader>
              <AccordionContent isOpen={openAccordions[i]}>
                <StyledParagraph>{item.answer}</StyledParagraph>
              </AccordionContent>
            </Accordion>
          ))}
        </div>
      );
    }

    return renderedNodes;
  };

  if (loading) return (
    <StyledLoadingContainer aria-label="Loading blog post">
      <StyledLoader />
    </StyledLoadingContainer>
  );
  if (error === "not-found") return <StyledError>Post not found.</StyledError>;
  if (error) return <StyledError>Unable to load post.</StyledError>;

  const authorName = "MilaajBrandset";

  return (
    <AnimationRevealPage>
      <Helmet>
        <title>{post.seo?.metaTitle || `${post.title} | MilaajBrandset Blog`}</title>
        <meta name="description" content={post.seo?.metaDescription || post.title} />
        {post.seo?.keywords && <meta name="keywords" content={post.seo.keywords} />}
      </Helmet>
      <Header from={post.seo?.metaTitle} />
      <Container>
        <TopSection>
          <LeftColumn>
            <DateText>
              {post.createdAt ? new Date(post.createdAt).toLocaleDateString() : ""}
            </DateText>
            <Title>{post.title}</Title>
          </LeftColumn>
          <RightColumn>
            <MetaDescription>{post.seo?.metaDescription || post.title}</MetaDescription>
          </RightColumn>
        </TopSection>
        <StyledImage
          src={
            post.featuredImage?.url?.startsWith("http")
              ? post.featuredImage.url
              : `${API_URL}${post.featuredImage?.url}` || "https://via.placeholder.com/800x400"
          }
          alt={post.featuredImage?.alt || post.title}
        />
        <Meta>
          <div>
            <Author>By {authorName}</Author>
          </div>
        </Meta>
        <Content>{renderRichText(post.content)}</Content>
      </Container>
    </AnimationRevealPage>
  );
};

export default SingleBlogPost;